import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Typography, TextField, IconButton, Button, Box } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { AppState } from "../../../../../redux";
import { OrganizationDiscount } from "../../../../../models/organizationSettingsData";
import { setLocalDiscounts } from "../../../../../redux/organizationSettings/actions";

/**
 *
 * Allows users to locally add and remove discount codes
 * from the organization's settings. Changes are reflected in the Redux store
 * but only saved permanently after form submission.
 *
 * @component OrganizationDiscountManagement
 */
const OrganizationDiscountManagement: React.FC = () => {
  const { t } = useTranslation(["organization", "snackbars", "common"]);
  const dispatch = useDispatch();

  const discountCodes = useSelector(
    (state: AppState) => state.organizationSettings.organizationSettings?.discounts || []
  );

  const [code, setCode] = useState("");

  /**
   * Adds a new discount code to the local Redux state.
   * Trims the input and prevents adding duplicates.
   */
  const handleAddCode = () => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;
    if (discountCodes.some((d) => d.code === trimmedCode)) return;

    const updatedDiscounts: OrganizationDiscount[] = [
      ...discountCodes,
      { code: trimmedCode },
    ];
    dispatch(setLocalDiscounts(updatedDiscounts));
    setCode("");
  };

  /**
   * Removes a discount code from the local Redux state.
   *
   * @param codeToRemove - The discount code to remove.
   */
  const handleRemoveCode = (codeToRemove: string) => {
    const updatedDiscounts = discountCodes.filter(
      (discount) => discount.code !== codeToRemove
    );
    dispatch(setLocalDiscounts(updatedDiscounts));
  };

  return (
    <div style={{ marginTop: "1rem" }} data-testid="discount-management">
      <Box>
        <h3>{t("discounts.organizationDiscounts", { ns: "organization" })}</h3>
      </Box>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <TextField
          variant="outlined"
          label={t("discounts.discountCodeInput", {
            ns: "organization",
            defaultValue: "Discount Code",
          })}
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <Button onClick={handleAddCode} color="primary" variant="contained">
          {t("discounts.addDiscount", { ns: "organization" })}
        </Button>
      </div>

      {discountCodes.length > 0 ? (
        discountCodes.map(({ code: discountCode, status }) => (
          <div
            key={discountCode}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0.5rem 1rem",
              borderBottom: "1px solid #ddd",
              backgroundColor: "#f9f9f9",
              marginBottom: "0.5rem",
            }}
          >
            <Typography style={{ flex: 1 }}>{discountCode}</Typography>
            {status ? (
              <Typography style={{ flex: 1, color: "#666", fontStyle: "italic" }}>
                {t(`discounts.status.${status}`, {
                  ns: "organization",
                })}
              </Typography>
            ) : null}
            <IconButton onClick={() => handleRemoveCode(discountCode)}>
              <DeleteIcon />
            </IconButton>
          </div>
        ))
      ) : (
        <Typography variant="body2" color="textSecondary">
          {t("discounts.noDiscounts", {
            ns: "organization",
            defaultValue: "No discounts yet.",
          })}
        </Typography>
      )}
    </div>
  );
};

export default OrganizationDiscountManagement;
